import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FeedbackButton from './FeedbackButton';
import NotificationBell from './NotificationBell';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, Collapse,
  IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Badge,
  Avatar, Chip, Menu, MenuItem, Tooltip
} from '@mui/material';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DashboardIcon from '@mui/icons-material/Dashboard';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import InboxIcon from '@mui/icons-material/Inbox';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaidIcon from '@mui/icons-material/Paid';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import api from '../services/api';

const DRAWER_WIDTH = 260;

const navGroups = {
  homeowner: [
    {
      group: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Inbox', icon: <InboxIcon />, path: '/inbox' },
        { text: 'Reminders & Schedule', icon: <CalendarTodayIcon />, path: '/schedule' },
      ]
    },
    {
      group: 'Pre-Construction',
      items: [
        { text: 'Vendors', icon: <BusinessIcon />, path: '/vendors' },
        { text: 'Scopes & Proposals', icon: <PeopleIcon />, path: '/proposal-review' },
      ]
    },
    {
      group: 'Contract to Pay',
      items: [
        { text: 'Contracts', icon: <DescriptionIcon />, path: '/contracts' },
        { text: 'Purchase Orders', icon: <ShoppingCartIcon />, path: '/purchase-orders' },
        { text: 'View Invoices', icon: <ReceiptIcon />, path: '/invoices' },
        { text: 'Submit Invoice', icon: <ReceiptIcon />, path: '/submit-invoice' },
        { text: 'Payments', icon: <PaidIcon />, path: '/payments' },
      ]
    },
    {
      group: 'Finance',
      items: [
        { text: 'Budget', icon: <AccountBalanceWalletIcon />, path: '/budget' },
        { text: 'Credit Memos', icon: <ReceiptLongIcon />, path: '/credit-memos' },
      ]
    },
    {
      group: 'Documents',
      items: [
        { text: 'Project Documents', icon: <FolderSharedIcon />, path: '/documents' },
      ]
    },
    {
      group: 'Settings',
      items: [
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ]
    },
  ],
  subcontractor: [
    {
      group: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
      ]
    },
    {
      group: '1 · Customers',
      items: [
        { text: 'My Customers', icon: <PeopleIcon />, path: '/customers' },
      ]
    },
    {
      group: '2 · Contracts',
      items: [
        { text: 'Contracts', icon: <DescriptionIcon />, path: '/contracts' },
        { text: 'Open Scopes', icon: <AssignmentIcon />, path: '/scopes' },
        { text: 'My Proposals', icon: <PeopleIcon />, path: '/proposals' },
      ]
    },
    {
      group: '3 · Get Paid',
      items: [
        { text: 'Accounts Receivable', icon: <AccountBalanceWalletIcon />, path: '/ar' },
        { text: 'View Invoices', icon: <ReceiptIcon />, path: '/invoices' },
        // { text: 'Submit Invoice', icon: <ReceiptIcon />, path: '/submit-invoice' }, // hidden for subs
      ]
    },
    {
      group: 'Settings',
      items: [
        { text: 'Company Profile', icon: <BusinessIcon />, path: '/company-profile' },
      ]
    },
  ],
  general_contractor: [
    {
      group: 'Overview',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Inbox', icon: <InboxIcon />, path: '/inbox' },
        { text: 'Projects', icon: <FolderIcon />, path: '/projects' },
        { text: 'Reminders & Schedule', icon: <CalendarTodayIcon />, path: '/schedule' },
      ]
    },
    {
      group: 'Pre-Construction',
      items: [
        { text: 'Vendors', icon: <BusinessIcon />, path: '/vendors' },
        { text: 'Scopes & Proposals', icon: <PeopleIcon />, path: '/proposal-review' },
      ]
    },
    {
      group: 'Contract to Pay',
      items: [
        { text: 'Contracts', icon: <DescriptionIcon />, path: '/contracts' },
        { text: 'Purchase Orders', icon: <ShoppingCartIcon />, path: '/purchase-orders' },
        { text: 'View Invoices', icon: <ReceiptIcon />, path: '/invoices' },
        { text: 'Submit Invoice', icon: <ReceiptIcon />, path: '/submit-invoice' },
        { text: 'Payments', icon: <PaidIcon />, path: '/payments' },
      ]
    },
    {
      group: 'Finance',
      items: [
        { text: 'Budget', icon: <AccountBalanceWalletIcon />, path: '/budget' },
        { text: 'Credit Memos', icon: <ReceiptLongIcon />, path: '/credit-memos' },
      ]
    },
  ],
  admin: [
    {
      group: 'Administration',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
        { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
        { text: 'Plans', icon: <CreditCardIcon />, path: '/admin/plans' },
        { text: 'Feedback', icon: <FeedbackIcon />, path: '/admin/feedback' },
        { text: 'Architecture', icon: <DescriptionIcon />, path: '/admin/architecture' },
      ]
    },
    {
      group: 'Legacy',
      items: [
        { text: 'GC Permissions', icon: <SettingsIcon />, path: '/admin/gc-settings' },
      ]
    },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    let active = true;
    api.get('/favorites')
      .then(r => { if (active) setFavorites(r.data || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [user?.id]);

  const isFavorited = (path) => favorites.some(f => f.path === path);

  const toggleFavorite = async (e, item) => {
    e.stopPropagation();
    const already = isFavorited(item.path);
    try {
      if (already) {
        await api.delete('/favorites', { params: { path: item.path } });
        setFavorites(prev => prev.filter(f => f.path !== item.path));
      } else {
        const res = await api.post('/favorites', {
          path: item.path, label: item.text, icon_name: item.iconName || null
        });
        setFavorites(prev => [...prev, res.data]);
      }
    } catch (err) {
      // silent - keep UI responsive
    }
  };

  const FAV_ICON = {
    Dashboard: <DashboardIcon />, Inbox: <InboxIcon />, Projects: <FolderIcon />,
    Schedule: <CalendarTodayIcon />, Scopes: <AssignmentIcon />, Proposals: <PeopleIcon />,
    Vendors: <BusinessIcon />, Contracts: <DescriptionIcon />,
    'Purchase Orders': <ShoppingCartIcon />, Budget: <AccountBalanceWalletIcon />,
    'View Invoices': <ReceiptIcon />, 'Submit Invoice': <ReceiptIcon />,
    Payments: <PaidIcon />, 'Credit Memos': <ReceiptLongIcon />,
    'Accounts Receivable': <AccountBalanceWalletIcon />,
    'Project Documents': <FolderSharedIcon />, Settings: <SettingsIcon />,
  };
  const [gcPermissions, setGcPermissions] = React.useState(null);
  const [inboxCount, setInboxCount] = React.useState(0);

  React.useEffect(() => {
    if (user?.role !== 'homeowner' && user?.role !== 'general_contractor') return;
    let active = true;
    const fetchCount = () => {
      import('../services/api').then(({ default: api }) => {
        api.get('/inbound?status=unassigned').then(r => {
          if (active) setInboxCount(Array.isArray(r.data) ? r.data.length : 0);
        }).catch(() => {});
      });
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  React.useEffect(() => {
    if (user?.role === 'general_contractor') {
      import('../services/api').then(({ default: api }) => {
        api.get('/admin/settings').then(r => {
          setGcPermissions(r.data?.gc_permissions || {});
        }).catch(() => setGcPermissions({}));
      });
    }
  }, [user]);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const go = (path) => { navigate(path); setMobileOpen(false); };
  const [anchorEl, setAnchorEl] = useState(null);
  const [navMenu, setNavMenu] = useState({ anchor: null, item: null });

  const handleNavContext = (e, item) => {
    e.preventDefault();
    setNavMenu({ anchor: e.currentTarget, item });
  };
  const closeNavMenu = () => setNavMenu({ anchor: null, item: null });

  const setAsDefaultLanding = async () => {
    const item = navMenu.item;
    closeNavMenu();
    if (!item) return;
    try {
      await api.put('/favorites/default-landing', { path: item.path });
      alert(item.text + ' is now your default landing page.');
    } catch (err) {
      alert('Failed to set default landing page.');
    }
  };

  const groups = navGroups[user?.role] || [];
  const [openGroups, setOpenGroups] = React.useState(() => {
    const init = {};
    (navGroups[user?.role] || []).forEach(g => { init[g.group] = true; });
    return init;
  });
  const toggleGroup = (group) => setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));

  const drawer = (
    <Box sx={{ height: '100%', background: 'linear-gradient(180deg, #1a237e 0%, #283593 100%)' }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#ff6f00', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>C</Typography>
        </Box>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Yaplar</Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ p: 2, mt: 1 }}>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#ff6f00', width: 36, height: 36 }}>
            {user?.first_name?.[0]}
          </Avatar>
          <Box>
            <Typography sx={{ color: 'white', fontSize: 14, fontWeight: 600 }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Chip label={user?.role?.replace('_', ' ')} size="small"
              sx={{ bgcolor: '#ff6f00', color: 'white', fontSize: 10, height: 18 }} />
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <IconButton size="small" onClick={() => { logout(); navigate('/login'); }}
              sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff5252' } }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <List sx={{ px: 1.5, mt: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
        {favorites.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <ListItemButton disableRipple sx={{ borderRadius: 2, py: 0.6, px: 1.5, mb: 0.25,
              color: 'rgba(255,255,255,0.45)', cursor: 'default',
              '&:hover': { bgcolor: 'transparent' } }}>
              <ListItemText primary={'FAVORITES'}
                primaryTypographyProps={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2 }} />
              <StarIcon sx={{ fontSize: 14, color: '#ff6f00' }} />
            </ListItemButton>
            {favorites.map((fav) => (
              <ListItem key={'fav-' + fav.path} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => go(fav.path)}
                  selected={location.pathname === fav.path}
                  sx={{ borderRadius: 2, py: 0.7, pl: 2.5,
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                      '& .MuiListItemIcon-root': { color: '#ff6f00' } },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white',
                      '& .fav-star': { opacity: 1 } } }}>
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 34, '& svg': { fontSize: 17 } }}>
                    {FAV_ICON[fav.label] || <StarIcon />}
                  </ListItemIcon>
                  <ListItemText primary={fav.label}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }} />
                  <IconButton size="small" className="fav-star"
                    onClick={(e) => toggleFavorite(e, { path: fav.path, text: fav.label })}
                    sx={{ opacity: 0.8, color: '#ff6f00', p: 0.3,
                      '&:hover': { color: '#ff8f33' } }}>
                    <StarIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mt: 0.5, mb: 0.25 }} />
          </Box>
        )}
        {groups.map((group) => (
          <Box key={group.group} sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => toggleGroup(group.group)}
              sx={{
                borderRadius: 2, py: 0.6, px: 1.5, mb: 0.25,
                color: 'rgba(255,255,255,0.45)',
                '&:hover': { bgcolor: 'transparent', color: 'rgba(255,255,255,0.7)' }
              }}>
              <ListItemText
                primary={group.group.toUpperCase()}
                primaryTypographyProps={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2 }} />
              {openGroups[group.group]
                ? <ExpandLessIcon sx={{ fontSize: 15 }} />
                : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
            </ListItemButton>
            <Collapse in={openGroups[group.group]} timeout="auto" unmountOnExit>
              {group.items.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                  <ListItemButton
                    onClick={() => go(item.path)}
                    onContextMenu={(e) => handleNavContext(e, item)}
                    selected={location.pathname === item.path}
                    sx={{
                      borderRadius: 2, py: 0.7, pl: 2.5,
                      color: 'rgba(255,255,255,0.7)',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        '& .MuiListItemIcon-root': { color: '#ff6f00' }
                      },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white', '& .nav-star': { opacity: 1 } }
                    }}>
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 34, '& svg': { fontSize: 17 } }}>
                      {item.path === '/inbox' && inboxCount > 0 ? (
                        <Badge badgeContent={inboxCount} color="error"
                          sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 16, minWidth: 16, top: 2, right: 2 } }}>
                          {item.icon}
                        </Badge>
                      ) : item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }} />
                    <IconButton size="small" className="nav-star"
                      onClick={(e) => toggleFavorite(e, item)}
                      sx={{ p: 0.3, color: isFavorited(item.path) ? '#ff6f00' : 'rgba(255,255,255,0.4)',
                        opacity: isFavorited(item.path) ? 1 : 0,
                        transition: 'opacity 0.15s',
                        '&:hover': { color: '#ff8f33' } }}>
                      {isFavorited(item.path)
                        ? <StarIcon sx={{ fontSize: 15 }} />
                        : <StarBorderIcon sx={{ fontSize: 15 }} />}
                    </IconButton>
                  </ListItemButton>
                </ListItem>
              ))}
            </Collapse>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mt: 0.5, mb: 0.25 }} />
          </Box>
        ))}
      </List>

    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      <AppBar position="fixed" elevation={0}
        sx={{ width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { sm: `${DRAWER_WIDTH}px` }, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: '#1a237e', fontWeight: 700 }}>
            {groups.flatMap(g => g.items).find(i => i.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <NotificationBell />
          <Tooltip title="Account">
            <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ bgcolor: '#1a237e', width: 35, height: 35 }}>{user?.first_name?.[0]}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem onClick={() => { logout(); navigate('/login'); }}>
              <LogoutIcon sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
          <Menu anchorEl={navMenu.anchor} open={Boolean(navMenu.anchor)} onClose={closeNavMenu}>
            <MenuItem onClick={setAsDefaultLanding}>
              <DashboardIcon sx={{ mr: 1, fontSize: 18 }} /> Set as default landing page
            </MenuItem>
            <MenuItem onClick={() => { const it = navMenu.item; closeNavMenu(); if (it) toggleFavorite({ stopPropagation: () => {} }, it); }}>
              <StarIcon sx={{ mr: 1, fontSize: 18, color: '#ff6f00' }} />
              {navMenu.item && isFavorited(navMenu.item.path) ? 'Remove from favorites' : 'Add to favorites'}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' } }} open>
          {drawer}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` }, mt: 8 }}>
        {children}
        <FeedbackButton />
      </Box>
    </Box>
  );
}
